import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';

const DEFAULT_STATE = {
  isPrintEnabled: false,
  isPrinting: false,
  textSize: 'md',
  isTextEnabled: false,
  ratio: 8 / 10,
  orientation: 'PORTRAIT',
};

export const PrintContext = createContext([DEFAULT_STATE]);

export default function PrintProvider({ children, service }) {
  const reducer = (state, action) => {
    switch (action.type) {
      case 'SET_RATIO': {
        return { ...state, ...{ ratio: action.payload } };
      }
      case 'SET_ORIENTATION': {
        return { ...state, ...{ orientation: action.payload } };
      }
      case 'SET_IS_PRINT_ENABLED': {
        return { ...state, ...{ isPrintEnabled: action.payload } };
      }
      case 'SET_IS_PRINTING': {
        return { ...state, ...{ isPrinting: action.payload } };
      }
      case 'SET_TEXT_SIZE': {
        return { ...state, ...{ textSize: action.payload } };
      }
      case 'SET_IS_TEXT_ENABLED': {
        return { ...state, ...{ isTextEnabled: action.payload } };
      }
      default:
        return action.payload;
    }
  };

  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

  const getState = useCallback(() => state, [state]);

  const setIsPrintEnabled = useCallback(
    (isPrintEnabled) =>
      dispatch({ type: 'SET_IS_PRINT_ENABLED', payload: isPrintEnabled }),
    [dispatch]
  );

  const setIsPrinting = useCallback(
    (isPrinting) => dispatch({ type: 'SET_IS_PRINTING', payload: isPrinting }),
    [dispatch]
  );

  const setTextSize = useCallback(
    (textSize) => dispatch({ type: 'SET_TEXT_SIZE', payload: textSize }),
    [dispatch]
  );

  const setIsTextEnabled = useCallback(
    (isTextEnabled) =>
      dispatch({ type: 'SET_IS_TEXT_ENABLED', payload: isTextEnabled }),
    [dispatch]
  );

  const setRatio = useCallback(
    (ratio) => dispatch({ type: 'SET_RATIO', payload: ratio }),
    [dispatch]
  );

  const setOrientation = useCallback(
    (orientation) =>
      dispatch({ type: 'SET_ORIENTATION', payload: orientation }),
    [dispatch]
  );

  /**
   * Sets the implementation of a modal service that can be used by extensions.
   *
   * @returns void
   */
  useEffect(() => {
    if (service) {
      service.setServiceImplementation({
        getState,
        setIsPrintEnabled,
        setIsPrinting,
        setTextSize,
        setIsTextEnabled,
        setRatio,
        setOrientation,
      });
    }
  }, [
    getState,
    service,
    setRatio,
    setIsPrintEnabled,
    setIsPrinting,
    setTextSize,
    setIsTextEnabled,
    setOrientation,
  ]);

  const api = {
    getState,
    setIsPrintEnabled,
    setIsPrinting,
    setTextSize,
    setIsTextEnabled,
    setRatio,
    setOrientation,
  };

  return (
    <PrintContext.Provider value={[state, api]}>
      {children}
    </PrintContext.Provider>
  );
}

PrintProvider.propTypes = {
  children: PropTypes.any,
  service: PropTypes.shape({
    setServiceImplementation: PropTypes.func,
  }).isRequired,
};

export const usePrint = () => useContext(PrintContext);
